import { ReasonCode } from '../constants/error-code-constants';

import type { InputErrorKey } from './InputErrorKey.types';

export type InputError = Record<InputErrorKey, string>;

/**
 * Machine-readable constraint values for a single field, keyed by the same error keys as
 * `parameters` (e.g. `stringLengthTooShort`). Each entry maps the validator's placeholder
 * names to their values — e.g. `{ stringLengthTooShort: { min: 12, max: 128 } }` — so the
 * client can localise via interpolation ("Mindestens {{min}} Zeichen") instead of relying on
 * the pre-formatted English string in `parameters`. CIN-13969.
 */
export type InputConstraints = Record<InputErrorKey, Record<string, string | number>>;

export type ApiErrorResponse<TInputFields = undefined> = {
    isError: boolean;
    /** Absent on the synthesised responses apiResponseHandler() builds for a null/Error result. */
    message?: string;
    data: null | {
        parameters: { [field in keyof TInputFields]: InputError };
        // Parallel to `parameters`, non-breaking: present only for error keys backed by a
        // parameterized validator (StringLength, range, count, …); omitted when there are none.
        constraints?: { [field in keyof TInputFields]?: InputConstraints };
        title: string;
        type: string;
        reason_code: ReasonCode;
        detail: string;
        status: number;
    };
    status: number;
};

/**
 * The rejection value produced by the fetch client (`fetch-helper.ts` `toFetchClientResponse`)
 * whenever a request resolves with a non-2xx status: a top-level `status`/`isError` plus the
 * parsed error body under `data`. This is the single canonical shape a `.catch()` or a React
 * Query `onError` handler receives, so callers should narrow unknowns with `isApiErrorResponse`
 * rather than re-declaring an ad-hoc local shape.
 */
export type ApiError = {
    isError?: boolean;
    status?: number;
    detail?: string;
    /**
     * The parsed error body, stamped with `isError` by the fetch client. Every problem+json field
     * is optional here because the client spreads whatever it managed to parse: a response with no
     * body, a non-JSON body, or a body that is not a problem+json document all yield just
     * `{ isError: true }`. Narrow before reading a field.
     */
    data?: (Partial<NonNullable<ApiErrorResponse['data']>> & { isError?: boolean }) | null;
};

/**
 * Narrow an unknown thrown/rejected value to {@link ApiError}: it is one when it carries an HTTP
 * error status (>= 400) or the explicit `isError` flag the fetch client stamps on failures.
 */
export function isApiErrorResponse(value: unknown): value is ApiError {
    if (!value || typeof value !== 'object') {
        return false;
    }
    const v = value as { status?: unknown; isError?: unknown };
    return (typeof v.status === 'number' && v.status >= 400) || v.isError === true;
}
